import { isValidTimeZone, wallClockIn } from './localTime.js';
/**
 * A zone that follows a real place's clock, including daylight saving.
 * Rejects fixed offsets and zones like `UTC`, `EST` or `Etc/GMT+8`, which
 * would read a period an hour wrong for half the year.
 */
export function isSiteZone(tz) {
    return typeof tz === 'string' && tz.length > 0 && tz.length <= 64
        && tz.includes('/') && !tz.startsWith('Etc/') && isValidTimeZone(tz);
}
/** True when the basis would read a time-of-use period to pick the rate. */
export function basisHasTimeOfUse(basis) {
    const t = basis.tariff;
    if (t?.type === 'time_of_use' && t.time_of_use_schedule.length > 0)
        return true;
    // A tariff with a usable base decides every start; inline windows never run.
    if (t && typeof t.energy_rate === 'number')
        return false;
    return (basis.tou_windows?.length ?? 0) > 0;
}
/**
 * The matching rules, given the wall clock the caller has decided on.
 * `clock === null` means no usable zone: a time-of-use branch then returns
 * `tou_without_timezone` and NEVER falls back to the server clock.
 */
export function matchElectricityRate(basis, clock) {
    const t = basis.tariff;
    if (t) {
        let rate = typeof t.energy_rate === 'number' ? t.energy_rate : null;
        let local = null;
        if (t.type === 'time_of_use' && t.time_of_use_schedule.length > 0) {
            if (!clock)
                return { status: 'tou_without_timezone' };
            const hit = t.time_of_use_schedule.find(e => e.day_of_week.includes(clock.day)
                && clock.hour >= e.start_hour && clock.hour < e.end_hour);
            if (hit) {
                rate = hit.energy_rate;
                local = { day: clock.day, hour: clock.hour };
            }
        }
        if (rate != null) {
            return { status: 'resolved', rate_cents: Math.round(rate), rate_type: t.rate_type ?? 'per_kwh', source: 'tariff', local };
        }
    }
    const windows = basis.tou_windows ?? [];
    if (windows.length > 0) {
        if (!clock)
            return { status: 'tou_without_timezone' };
        const hit = windows.find(w => w.day_of_week.includes(clock.day)
            && clock.hour >= w.start_hour && clock.hour < w.end_hour);
        if (hit) {
            return { status: 'resolved', rate_cents: Math.round(hit.rate_cents), rate_type: hit.rate_type ?? 'per_kwh',
                source: 'inline_tou', local: { day: clock.day, hour: clock.hour } };
        }
    }
    if (typeof basis.flat_rate_cents === 'number') {
        return { status: 'resolved', rate_cents: basis.flat_rate_cents, rate_type: basis.flat_rate_type ?? 'per_kwh',
            source: 'flat', local: null };
    }
    return { status: 'resolved', rate_cents: null, rate_type: 'per_kwh', source: 'none', local: null };
}
/** The reimbursement rate at instant `at`, reading periods in `basis.time_zone`. */
export function resolveReimbursementRateAt(basis, at) {
    const clock = isSiteZone(basis.time_zone) ? wallClockIn(at, basis.time_zone) : null;
    return matchElectricityRate(basis, clock ? { day: clock.day, hour: clock.hour } : null);
}
//# sourceMappingURL=electricityRateAt.js.map